import { FocusEvent, KeyboardEvent, useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client/react'

import { CheckIcon } from '@pleio/icons/check'
import { CrossIcon } from '@pleio/icons/cross'
import { EditSmallIcon } from '@pleio/icons/edit-small'

import Flexer from 'js/components/Flexer/Flexer'
import FormItem from 'js/components/Form/FormItem'
import IconButton from 'js/components/IconButton'
import Spacer from 'js/components/Spacer/Spacer'
import { MAX_LENGTH, REGEX } from 'js/lib/constants'

import styles from './TitleSlugField.module.css'
import { GET_SLUG_SUGGESTION } from './TitleSlugField.queries'
import { TitleSlugFieldProps } from './TitleSlugField.types'

type CustomSlugFormProps = {
    defaultValue: string
    setIsEditing: (isEditing: boolean) => void
    onSubmit: (customSlug: string) => void
}

const CustomSlugForm = ({
    defaultValue,
    setIsEditing,
    onSubmit,
}: CustomSlugFormProps) => {
    const { t } = useTranslation()

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, errors: localErrors },
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            customSlug: defaultValue,
        },
    })

    const handleSubmitForm = ({ customSlug }: { customSlug: string }) => {
        onSubmit(customSlug)
    }

    const submitForm = handleSubmit(handleSubmitForm)

    const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Allow traversing, editing the field with meta, arrow and edit keys
        if (
            REGEX.navKey.test(event.key) ||
            REGEX.editKey.test(event.key) ||
            REGEX.metaKey.test(event.key)
        ) {
            return
        }
        // Allow only safe characters for slugs
        if (!REGEX.slugInputChar.test(event.key)) {
            event.preventDefault()
        }
        // Submit on Enter
        if (event.key === 'Enter') {
            return submitForm()
        }
    }

    return (
        <div className={styles.slugFieldForm}>
            <FormItem
                size="small"
                control={control}
                type="text"
                name="customSlug"
                label={t('form.url')}
                aria-label={t('form.custom-url')}
                helper={t('form.custom-url-helper')}
                errors={localErrors}
                maxLength={MAX_LENGTH.title}
                rules={{
                    pattern: {
                        value: REGEX.slug,
                        message: t('error.invalid-slug'),
                    },
                }}
                ElementBefore={
                    <span className={styles.slugFieldFormUrl}>
                        {window.location.host}/
                    </span>
                }
                className={styles.slugFieldFormTextField}
                onKeyDown={handleOnKeyDown}
                formatValue={(value: string) => {
                    const newValue = value
                        .trimStart()
                        .replace(/ /g, '-') // Replace space by hyphen
                        .replace(/--+/g, '-') // Remove double hyphen
                        .toLowerCase()
                    return newValue
                }}
            />
            <Flexer
                gutterSize="sm"
                divider
                dividerOffset="sm"
                style={{ marginTop: 2 }}
            >
                <IconButton
                    size="normal"
                    variant={isDirty ? 'primary' : 'tertiary'}
                    radiusStyle="rounded"
                    tooltip={t('action.save')}
                    aria-label={t('form.save-custom-url')}
                    onClick={submitForm}
                    disabled={!isValid || !isDirty}
                >
                    <CheckIcon />
                </IconButton>
                <IconButton
                    size="normal"
                    variant="secondary"
                    radiusStyle="rounded"
                    tooltip={t('action.cancel')}
                    aria-label={t('form.cancel-custom-url')}
                    onClick={() => {
                        setIsEditing(false)
                    }}
                >
                    <CrossIcon />
                </IconButton>
            </Flexer>
        </div>
    )
}

const TitleSlugField = ({ entity, urlSlugEnabled }: TitleSlugFieldProps) => {
    const { t } = useTranslation()

    const {
        control,
        getValues,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext()

    const watchUrlSlug = watch('urlSlug')

    const [getSlugSuggestion, { loading, data }] =
        useLazyQuery<any>(GET_SLUG_SUGGESTION)

    useEffect(() => {
        const slugSuggestion = data?.entitySlugSuggestion
        if (slugSuggestion?.available) {
            setValue('urlSlug', slugSuggestion.suggestion) // Set slug
        }
    }, [data, setValue, getValues])

    const handleOnBlurTitle = (e: FocusEvent<HTMLInputElement>) => {
        const value = e.target.value
        const newValue = value.trim().replace(/\s\s+/g, ' ') // Remove trailing and double spaces

        setValue('title', newValue)

        if (!urlSlugEnabled) {
            return
        }

        // Don't overwrite if entity slug exists or if custom slug is set
        if (getValues('urlSlugIsCustom') || entity?.urlSlug) {
            return
        }

        getSlugSuggestion({
            variables: {
                guid: entity?.guid,
                title: newValue,
            },
        })
    }

    const handleOnSubmitSlug = (val: string) => {
        const customSlug = val
        const urlSlug = entity?.urlSlug

        // If a custom slug is set and differs from entity slug, ask for confirmation
        if (!!customSlug && !!urlSlug && customSlug !== urlSlug) {
            if (!confirm(t('form.custom-url-confirm'))) {
                return
            }
        }

        setValue('urlSlugIsCustom', !!customSlug) // Keep track of whether the slug is custom or not
        getSlugSuggestion({
            variables: {
                guid: entity?.guid,
                title:
                    customSlug || // Get slug suggestion based on input
                    entity?.urlSlug || // ..if empty use existing urlSlug
                    getValues('title'), // ..or get slug based on title
            },
        })
        setIsEditing(false)
    }

    const [isEditing, setIsEditing] = useState(false)

    return (
        <Spacer spacing="tiny" className={styles.wrapper}>
            <FormItem
                control={control}
                type="text"
                name="title"
                title={t('form.title')}
                required
                errors={errors}
                onBlur={handleOnBlurTitle}
            />

            {urlSlugEnabled && (
                <>
                    {isEditing ? (
                        <CustomSlugForm
                            defaultValue={getValues('urlSlug')}
                            setIsEditing={setIsEditing}
                            onSubmit={handleOnSubmitSlug}
                        />
                    ) : (
                        <div className={styles.slugFieldWrapper}>
                            <span>{t('form.url')}:</span>
                            <span className={styles.slugFieldUrl}>
                                {window.location.host}/{watchUrlSlug}
                            </span>
                            <IconButton
                                size="small"
                                variant="secondary"
                                radiusStyle="rounded"
                                tooltip={t('action.edit')}
                                aria-label={t('form.edit-custom-url')}
                                onClick={() => {
                                    setIsEditing(true)
                                }}
                                loading={loading}
                            >
                                <EditSmallIcon />
                            </IconButton>
                        </div>
                    )}
                </>
            )}
        </Spacer>
    )
}

export default TitleSlugField
